import { useEffect, useRef, useState } from "react";
import { glassStyle } from "../data/utils";
import { VIEW_OPTIONS, type ViewKey } from "../types";

type Props = {
  active: ViewKey;
  onChange: (v: ViewKey) => void;
};

export function NavDropdown({ active, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const activeLabel =
    VIEW_OPTIONS.find((o) => o.key === active)?.label ?? "Map";

  return (
    <div
      ref={ref}
      style={{ position: "fixed", top: 16, left: 16, zIndex: 20 }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          ...glassStyle,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderRadius: 14,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <InfinityLogo />

        <span>{activeLabel}</span>
        <span style={{ opacity: 0.7, fontSize: 10 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div
          style={{
            ...glassStyle,
            marginTop: 8,
            padding: 6,
            borderRadius: 14,
            minWidth: 200,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {VIEW_OPTIONS.map((opt) => {
            const isActive = opt.key === active;
            return (
              <button
                key={opt.key}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
                style={{
                  textAlign: "left",
                  background: isActive
                    ? "rgba(139,92,246,0.22)"
                    : "transparent",
                  border: "none",
                  color: "#fff",
                  padding: "10px 12px",
                  borderRadius: 10,
                  fontSize: 14,
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background =
                      "transparent";
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfinityLogo() {
  return (
    <span
      aria-hidden
      style={{
        width: 28,
        height: 28,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
      }}
    >
      <svg
        viewBox="0 0 64 32"
        width="28"
        height="28"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="infinityGrad" x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#7DD9FF" />
            <stop offset="35%" stopColor="#6B8DFF" />
            <stop offset="65%" stopColor="#8E63FF" />
            <stop offset="100%" stopColor="#E8B6FF" />
          </linearGradient>
          <linearGradient
            id="infinityHighlight"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
            <stop offset="60%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {/* Infinity body */}
        <path
          d="M16 16 C16 6, 32 6, 32 16 S48 26, 48 16 S32 6, 32 16 S16 26, 16 16 Z"
          fill="none"
          stroke="url(#infinityGrad)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Glossy top highlight */}
        <path
          d="M16 16 C16 6, 32 6, 32 16 S48 26, 48 16 S32 6, 32 16 S16 26, 16 16 Z"
          fill="none"
          stroke="url(#infinityHighlight)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(0, -1.2)"
          opacity="0.85"
        />
      </svg>
    </span>
  );
}
